import React from "react";

function fmtMoney(amount, currency = "CAD") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount ?? "—"} ${currency}`.trim();
  return `${n.toFixed(2)} ${String(currency || "CAD").toUpperCase()}`.trim();
}

function labelStatus(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "Confirmée";
  if (s === "pending") return "En attente";
  if (s === "failed") return "Rejetée";
  if (s === "cancelled") return "Annulée";
  return status || "—";
}

function isTransferRecord(tx) {
  if (tx?.sender || tx?.senderWallet || tx?.recipient) return true;
  if (tx?.senderUserId != null && tx?.recipientPhone) return true;
  return false;
}

export default function AdminWalletDetailsModal({ open, tx, onClose }) {
  if (!open || !tx) return null;

  const transfer = isTransferRecord(tx);

  const reference = tx.reference || "—";
  const status = labelStatus(tx.status);

  const currency = String(tx.currency || "CAD").toUpperCase();
  const feeCurrency = String(tx.feeCurrency || "CAD").toUpperCase();

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Détails opération wallet"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="w-full max-w-5xl rounded-2xl bg-white dark:bg-slate-900 shadow-xl border border-black/10 dark:border-white/10 overflow-hidden max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-black/10 dark:border-white/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {transfer ? "Détails du transfert" : "Détails de la transaction"}
              </h3>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400 break-words">
                Ref: <b>{reference}</b> • {status}
              </p>
            </div>

            <button
              onClick={onClose}
              className="rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10
                         hover:bg-black/5 dark:hover:bg-white/10"
            >
              Fermer
            </button>
          </div>
        </div>

        <div className="p-5 overflow-y-auto">
          {!transfer ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Card title="Résumé">
                <Info label="Type" value={prettyType(tx.type)} />
                <Info label="Montant" value={fmtMoney(tx.amount, currency)} />
                <Info label="Devise ledger" value={currency} />
                <Info label="Méthode" value={tx.method || "—"} />
                <Info label="Statut" value={status} />
                <Info label="Référence" value={reference} />
                <Info label="Note" value={tx.note || "—"} />
              </Card>

              <Card title="Utilisateur">
                <Info label="Nom complet" value={tx.user?.fullName || "—"} />
                <Info label="Prénom" value={tx.user?.firstName || "—"} />
                <Info label="Nom" value={tx.user?.lastName || "—"} />
                <Info label="Email" value={tx.user?.email || "—"} />
                <Info label="Téléphone" value={tx.user?.phone || "—"} />
                <Info label="User ID" value={tx.user?.id ?? "—"} />
                <Info label="Pays" value={tx.user?.countryCode || "—"} />
                <Info
                  label="Devise affichage"
                  value={tx.user?.displayCurrency || "—"}
                />
                <Info label="Locale" value={tx.user?.locale || "—"} />
              </Card>

              <Card title="Wallet">
                <Info label="Wallet ID" value={tx.wallet?.id ?? "—"} />
                <Info
                  label="Solde actuel"
                  value={
                    tx.wallet?.balance != null
                      ? fmtMoney(tx.wallet.balance, tx.wallet.currency || "CAD")
                      : "—"
                  }
                />
                <Info
                  label="Hold actuel"
                  value={
                    tx.wallet?.heldBalance != null
                      ? fmtMoney(tx.wallet.heldBalance, tx.wallet.currency || "CAD")
                      : "—"
                  }
                />
                <Info
                  label="Devise wallet"
                  value={tx.wallet?.currency || "CAD"}
                />
                <Info
                  label="Solde avant"
                  value={
                    tx.balanceBefore != null
                      ? fmtMoney(tx.balanceBefore, "CAD")
                      : "—"
                  }
                />
                <Info
                  label="Solde après"
                  value={
                    tx.balanceAfter != null
                      ? fmtMoney(tx.balanceAfter, "CAD")
                      : "—"
                  }
                />
              </Card>

              <Card title="FX / Montants">
                <Info
                  label="Montant ledger"
                  value={fmtMoney(tx.amount, tx.currency || "CAD")}
                />
                <Info
                  label="Montant converti"
                  value={
                    tx.convertedAmount != null
                      ? fmtMoney(tx.convertedAmount, tx.fxToCurrency || currency)
                      : "—"
                  }
                />
                <Info label="FX rate" value={tx.fxRate ?? "—"} />
                <Info label="FX from" value={tx.fxFromCurrency || "—"} />
                <Info label="FX to" value={tx.fxToCurrency || "—"} />
                <Info
                  label="Fee"
                  value={
                    tx.feeAmount != null
                      ? fmtMoney(tx.feeAmount, feeCurrency)
                      : "—"
                  }
                />
              </Card>

              <Card title="Validation / Horodatage">
                <Info
                  label="Créé le"
                  value={tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "—"}
                />
                <Info
                  label="Mis à jour"
                  value={tx.updatedAt ? new Date(tx.updatedAt).toLocaleString() : "—"}
                />
                <Info label="Approuvé par" value={tx.approvedBy ?? "—"} />
                <Info
                  label="Date approbation"
                  value={tx.approvedAt ? new Date(tx.approvedAt).toLocaleString() : "—"}
                />
                <Info label="Rejeté par" value={tx.rejectedBy ?? "—"} />
                <Info
                  label="Date rejet"
                  value={tx.rejectedAt ? new Date(tx.rejectedAt).toLocaleString() : "—"}
                />
                <Info
                  label="Motif rejet"
                  value={tx.rejectionReason || "—"}
                />
                <Info
                  label="Idempotency key"
                  value={tx.idempotencyKey || "—"}
                />
              </Card>

              <Card title="Metadata">
                <JsonBox value={tx.metadata} />
              </Card>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <Card title="Résumé">
                <Info label="Type" value="Transfert wallet" />
                <Info label="Montant" value={fmtMoney(tx.amount, currency)} />
                <Info
                  label="Fee"
                  value={fmtMoney(tx.feeAmount || 0, tx.feeCurrency || "CAD")}
                />
                <Info label="Statut" value={status} />
                <Info label="Référence" value={reference} />
                <Info label="Note" value={tx.note || "—"} />
              </Card>

              <Card title="Expéditeur">
                <Info label="Nom complet" value={tx.sender?.fullName || "—"} />
                <Info label="Prénom" value={tx.sender?.firstName || "—"} />
                <Info label="Nom" value={tx.sender?.lastName || "—"} />
                <Info label="Email" value={tx.sender?.email || "—"} />
                <Info label="Téléphone" value={tx.sender?.phone || "—"} />
                <Info label="User ID" value={tx.sender?.id ?? tx.senderUserId ?? "—"} />
                <Info label="Pays" value={tx.sender?.countryCode || "—"} />
                <Info
                  label="Devise affichage"
                  value={tx.sender?.displayCurrency || "—"}
                />
                <Info label="Locale" value={tx.sender?.locale || "—"} />
              </Card>

              <Card title="Wallet expéditeur">
                <Info label="Wallet ID" value={tx.senderWallet?.id ?? tx.senderWalletId ?? "—"} />
                <Info
                  label="Solde actuel"
                  value={
                    tx.senderWallet?.balance != null
                      ? fmtMoney(tx.senderWallet.balance, tx.senderWallet.currency || "CAD")
                      : "—"
                  }
                />
                <Info
                  label="Hold actuel"
                  value={
                    tx.senderWallet?.heldBalance != null
                      ? fmtMoney(
                          tx.senderWallet.heldBalance,
                          tx.senderWallet.currency || "CAD"
                        )
                      : "—"
                  }
                />
                <Info
                  label="Devise wallet"
                  value={tx.senderWallet?.currency || "CAD"}
                />
              </Card>

              <Card title="Bénéficiaire">
                <Info label="Nom complet" value={tx.recipient?.fullName || "—"} />
                <Info label="Email" value={tx.recipient?.email || "—"} />
                <Info label="Téléphone" value={tx.recipient?.phone || "—"} />
                <Info
                  label="Type bénéficiaire"
                  value={tx.recipient?.isInternalUser ? "Interne" : "Externe / manuel"}
                />
                <Info
                  label="Recipient user ID"
                  value={tx.recipient?.id ?? tx.recipientUserId ?? "—"}
                />
                <Info label="Pays" value={tx.recipient?.countryCode || "—"} />
                <Info
                  label="Devise affichage"
                  value={tx.recipient?.displayCurrency || "—"}
                />
                <Info label="Locale" value={tx.recipient?.locale || "—"} />
              </Card>

              <Card title="Validation / Horodatage">
                <Info
                  label="Créé le"
                  value={tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "—"}
                />
                <Info
                  label="Mis à jour"
                  value={tx.updatedAt ? new Date(tx.updatedAt).toLocaleString() : "—"}
                />
                <Info
                  label="Complété le"
                  value={tx.completedAt ? new Date(tx.completedAt).toLocaleString() : "—"}
                />
                <Info label="Approuvé par" value={tx.approvedBy ?? "—"} />
                <Info
                  label="Date approbation"
                  value={tx.approvedAt ? new Date(tx.approvedAt).toLocaleString() : "—"}
                />
                <Info label="Rejeté par" value={tx.rejectedBy ?? "—"} />
                <Info
                  label="Date rejet"
                  value={tx.rejectedAt ? new Date(tx.rejectedAt).toLocaleString() : "—"}
                />
                <Info
                  label="Motif rejet"
                  value={tx.rejectionReason || "—"}
                />
                <Info
                  label="Idempotency key"
                  value={tx.idempotencyKey || "—"}
                />
              </Card>

              <Card title="Metadata">
                <JsonBox value={tx.metadata} />
              </Card>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-black/10 dark:border-white/10">
          <button
            onClick={onClose}
            className="w-full rounded-2xl bg-slate-900 text-white py-3 font-medium
                       hover:opacity-95 transition dark:bg-white dark:text-slate-900"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4">
      <div className="text-sm font-semibold text-slate-900 dark:text-white">
        {title}
      </div>
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-sm font-medium text-slate-900 dark:text-white break-words text-right max-w-[65%]">
        {value}
      </div>
    </div>
  );
}

function JsonBox({ value }) {
  const hasValue =
    value !== null &&
    value !== undefined &&
    !(typeof value === "object" && Object.keys(value).length === 0);

  return (
    <div className="rounded-xl border border-black/5 dark:border-white/10 bg-white/70 dark:bg-slate-900/50 p-3">
      <pre className="text-xs text-slate-700 dark:text-slate-200 whitespace-pre-wrap break-words">
        {hasValue ? JSON.stringify(value, null, 2) : "—"}
      </pre>
    </div>
  );
}

function prettyType(type) {
  const t = String(type || "").toLowerCase();
  if (t === "fund") return "Recharge";
  if (t === "withdraw") return "Retrait";
  if (t === "loan") return "Prêt";
  if (t === "transfer_out") return "Transfert sortant";
  if (t === "transfer_in") return "Transfert entrant";
  return type || "—";
}